import { FileTypes } from 'types/baseTypes';
import {
  ConnectPayoutStatus,
  PAYOUT_TXNS_HELD_IN_RESERVE_TABS,
  PAYOUT_TXNS_TABS,
  PAYOUT_TXNS_TYPE_FILTERS
} from './constants';
import { TeamIdPayload } from 'containers/home/home.model';

export interface PayoutsPayload {
  teamId: number;
  params: {
    status: ConnectPayoutStatus[];
    fromTimestamp: string;
    toTimestamp: string;
    search: string;
    limit: number;
    page: number;
    type: FileTypes;
    selectedIds: string[];
    amount: string;
  };
}

export interface Metadata {
  limit: number;
  page: number;
  total: number;
}
export interface PayoutsResponse {
  data: ConnectPayout[];
  metadata: Metadata;
}

export interface Payout {
  id: string;
  paymentMethod: string;
  currency: string;
  description: string;
  amount: number;
  grossAmount: number;
  serviceFee: number;
  pastRefundAmount: number;
  pastChargebackAmount: number;
  creditsApplied: number;
  status: string;
  merchantID: string;
  destination: string;
  createdAt: string;
  teamID: number;
}

export interface PayoutSummaryResponse {
  payoutSummary: PayoutSummary;
}

export interface PayoutSummary {
  payoutSettlement: payoutSettlement;
  payoutOnHold: PayoutOnHold;
  payoutBalance: PayoutBalance;
  isUnitEnabled: boolean;
}

export interface payoutSettlement {
  grossAmount: number;
  Amount: number;
  serviceFee: number;
  refunds: number;
  chargebacks: number;
  totalTrnxCount: number;
  creditsApplied: number;
}

export interface PayoutOnHold {
  grossAmount: number;
  Amount: number;
  serviceFee: number;
  refunds: number;
  chargebacks: number;
  totalTrnxCount: number;
}

export interface PayoutBalance {
  grossAmount: number;
  Amount: number;
  refunds: number;
  chargebacks: number;
  onHold: number;
  totalTrnxCount: number;
}

export interface PayoutsSummaryTransactions {
  transactions: PayoutTransaction[];
  totalCount: number;
}

export interface PayoutTransaction {
  id: string;
  paymentMethod: string;
  amount: number;
  status: string;
  createdAt: string;
  reason: string;
  source: string;
  customerName: string;
  profilePicture: string;
  merchantName: string;
  merchantId: string;
  parentId: string;
  refundTransactions: any;
}

export interface PayoutsSummaryTxnPayload {
  teamId: number;
  transactionType: PayoutTransactionTypes;
  status: PayoutStatus[];
  start: string;
  end: string;
  search: string;
  limit: number;
  page: number;
}

export interface PayoutByIdResponse {
  ID: string;
  paymentMethod: string;
  pgTransactionId: string;
  currency: string;
  descrption: string;
  amount: string;
  grossAmount: string;
  pastRefundAmount: string;
  pastChargebackAmount: string;
  creditsApplied: string;
  serviceFee: string;
  status: string;
  merchantId: string;
  destination: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  transactions: PayoutTransaction[];
}

export interface PayoutByIdPayload {
  payoutId: string;
  teamId: number;
}
export interface PayoutSummaryPayload {
  teamId: number;
  status: PayoutStatus[];
  start: string;
  end: string;
  search: string;
}

export interface ConnectPayout {
  id: string;
  amount: string;
  status: string;
  paymentSystem: string;
  createdAt: string;
  updatedAt: string;
  arrivalAt: string;
  isInstantPayout: boolean;
}

export interface InTransitPayoutsResponse {
  data: ConnectPayout[];
}

export interface InTransitPayoutsPayload {
  teamId: number;
}

export interface UpcomingPayoutsSummary {
  paymentsCollected: string;
  feesCollected: string;
  refunds: string;
  adjustments: string;
  creditsApplied: string;
  inFutureAmount: string;
  capitalRepaymentAmount: string;
  instantPayoutAmount: string;
  instantPayoutFee: string;
  keyLabelMapping: KeyLabelMappingItems[];
  payoutSendingVia: string;
}

export interface KeyLabelMappingItems {
  key: string;
  label: string;
  value: string;
  doNotShowWhenZero: boolean;
  isFinal: boolean;
}
export interface PayoutsAccountSummary {
  balance: string;
  inFuture: string;
  inTransit: string;
  inReserve: string;
  hasUpcomingPayouts: boolean;
  hasInTransitPayouts: boolean;
  hasInReservePayouts: boolean;
  hasReservedFundsData: boolean;
}

export enum PayoutStatus {
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  ON_HOLD = 'ON_HOLD',
  FAILED = 'FAILED',
  SCHEDULED = 'SCHEDULED'
}

export enum PayoutTransactionTypes {
  ON_HOLD = 'onhold',
  SETTLED = 'settled',
  BALANCE = 'balance'
}

export enum PayoutSummaryTypes {
  ON_HOLD = 'On hold',
  SETTLED = 'Total Settled',
  BALANCE = 'Balance'
}

export enum PayoutsSummaryTypes {
  HELD_IN_RESERVE = 'held-in-reserve',
  UPCOMING_PAYOUTS = 'upcoming-payouts',
  ON_THE_WAY = 'in-transit-payouts'
}

export interface ConnectPayoutByIdPayload {
  teamId: number;
  payoutId: string;
}

export interface ConnectPayoutByIdResponse {
  data: PayoutData;
  timeline: PayoutTimeline[];
  keyLabelMapping: KeyLabelMappingItems[];
}

export interface PayoutData {
  ID: string;
  paymentMethod: string;
  paymentTraceId: string;
  currency: string;
  description: string;
  amount: string;
  grossAmount: string;
  serviceFee: string;
  pastRefundAmount: string;
  pastChargebackAmount: string;
  creditsApplied: string;
  status: string;
  merchantID: string;
  destination: string;
  destinationBank: string;
  createdAt: string;
  updatedAt: string;
  arrivalAt: string;
  teamID: number;
  transactionFeePercentage: string;
  transactionFeeBasePoints: string;
  createdBy: string;
  notes: string;
  isInstantPayout: boolean;
  instantPayoutAmount: string;
  instantPayoutFee: string;
  capitalRepaymentAmount: string;
}

export interface UpdateNotesPayload extends ConnectPayoutByIdPayload {
  body: {
    notes: string;
  };
}

export interface PayoutTimeline {
  event: string;
  timestamp: string;
  performedBy: string;
  reason: string;
}

export interface TransactionsByPayoutIdPayload {
  amount: string;
  status: string;
  fromTimestamp: string;
  toTimestamp: string;
  page: number;
  limit: number;
  payoutId: string;
  teamId: number;
  search: string;
}

export interface UpcomingPayoutTransactionsPayload {
  amount: string;
  status: string;
  fromTimestamp: string;
  toTimestamp: string;
  page: number;
  limit: number;
  teamId: number;
  search: string;
}

export interface ConnectPayoutTransactionsResponse {
  data: PayoutTxnsData[];
  metadata: Metadata;
}

export interface PayoutTxnsData {
  ledgerId: number;
  amount: string;
  direction: string;
  createdAt: string;
  title: string;
  subTitle: string;
  description: string;
  ctaName: string;
  ctaType: string;
  ctaAction: string;
  hasAccess: boolean;
}

export interface Metadata {
  page: number;
  limit: number;
  total: number;
}

export interface PayoutTxnsPageState {
  tab: PAYOUT_TXNS_TABS;
  amountFilter: { title: string; min: string; max: string };
  typeFilter: PAYOUT_TXNS_TYPE_FILTERS[];
  search: string;
}

export interface ReservedFund {
  id: number;
  transactionAmount: string;
  reservedAmount: string;
  status: string;
  transactionId: string;
  createdAt: string;
  releaseDate: string;
  hasAccess: boolean;
  merchantId: string;
  comment: string;
}

export interface ReservedFundsResponse {
  data: ReservedFund[];
  totalAmount: string;
  totalCount: number;
}

export interface ReservedFundsPayload extends TeamIdPayload {
  page: number;
  limit: number;
  status?: string;
  createdStart?: string;
  createdEnd?: string;
  availableStart?: string;
  availableEnd?: string;
  amount?: string;
  transactionId?: string;
}

export interface HeldInReservePageState {
  amount: { title: string; min: string; max: string };
  transactionId: string;
  status?: string;
  createdDateFilter?: DateFilter;
  availableDateFilter?: DateFilter;
  tab: PAYOUT_TXNS_HELD_IN_RESERVE_TABS;
}

export interface DateFilter {
  option: string;
  startDate: string | null;
  endDate: string | null;
}

export interface AmountValue {
  min: string;
  max: string;
}

export interface AmountFilterValue {
  title: string;
  min: string;
  max: string;
}

export interface PaymentScheduleTimelineEvent {
  event: string;
  subtext: string;
  color: string;
}

export interface PaymentScheduleAppItem {
  collectedOn: string;
  arrivesInBank: string;
  timeline: PaymentScheduleTimelineEvent[];
}

export interface PaymentScheduleWebItem {
  collectedOn: string;
  closeOfDay: string;
  payoutCreatedOn: string;
  arrivesInBank: string;
}

export interface PaymentScheduleResponse {
  Data: {
    headingA: string;
    headingB: string;
    app: PaymentScheduleAppItem[];
    web: PaymentScheduleWebItem[];
  };
  StatusCode: number;
}
